import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";

import { formatPercent } from "@/features/matches";

import type { PlayerSummary } from "../../model/PlayerSummary";

const RANKED = msg({ message: "{count, plural, one {# ranked} other {# ranked}}" });
const ANALYZED = msg({
  message: "{count, plural, one {match in Relock} other {matches in Relock}}",
});

type Stat = { key: string; label: ReactNode; value: string; details: ReactNode };

type ProfileStatsProps = {
  summary: PlayerSummary;
  // Матчей с готовым разбором; пока не загрузилось — плитки нет.
  analyzedMatches: number | undefined;
};

// Полоса итогов внизу шапки: матчи, доля побед, время в игре и разобранные матчи — за всё
// время. Время появляется после пересчёта сводки (в старых сводках его нет).
export const ProfileStats = ({ summary, analyzedMatches }: ProfileStatsProps) => {
  const { i18n, t } = useLingui();
  const number = new Intl.NumberFormat(i18n.locale);
  const duration = (unit: "hour" | "minute", value: number) =>
    new Intl.NumberFormat(i18n.locale, {
      style: "unit",
      unit,
      unitDisplay: "short",
      maximumFractionDigits: 0,
    }).format(value);

  const wins = number.format(summary.wins);
  const matches = number.format(summary.matches);
  const stats: Stat[] = [
    {
      key: "matches",
      label: t`Matches`,
      value: matches,
      details: i18n._({ ...RANKED, values: { count: summary.ranked_matches } }),
    },
    {
      key: "wins",
      label: t`Wins`,
      value: formatPercent(summary.wins / summary.matches, i18n.locale),
      details: t`${wins} of ${matches}`,
    },
  ];
  if (summary.play_time_s > 0) {
    const perMatch = duration("minute", summary.play_time_s / 60 / summary.matches);
    stats.push({
      key: "time",
      label: t`In game`,
      value: duration("hour", summary.play_time_s / 3600),
      details: t`≈ ${perMatch} per match`,
    });
  }
  if (analyzedMatches !== undefined) {
    stats.push({
      key: "analyzed",
      label: t`Analyzed`,
      value: number.format(analyzedMatches),
      details: i18n._({ ...ANALYZED, values: { count: analyzedMatches } }),
    });
  }

  return (
    <dl className="grid grid-cols-[repeat(auto-fit,minmax(min(150px,100%),1fr))] border-t border-border bg-sidebar">
      {stats.map((stat) => (
        <div key={stat.key} data-stat={stat.key} className="flex flex-col px-7 py-3.5">
          <dt className="text-[13px] text-subtle">{stat.label}</dt>
          <dd className="font-mono text-[26px] leading-tight font-semibold">{stat.value}</dd>
          <dd className="text-xs text-subtle">{stat.details}</dd>
        </div>
      ))}
    </dl>
  );
};
